import { DateTimeFormatter, type LocalDate } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import constate from "constate";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PlatformValue, PostDto } from "shared";
import { useEventCallback } from "usehooks-ts";
import { LinkGen } from "../LinkGen";
import {
	getMonthGridDays,
	getPostDate,
	getWeekGridDays,
} from "./calendar.utils";

export type CalendarViewType = "calendar" | "list";
export type CalendarType = "week" | "month";

interface CalendarProviderProps {
	date: LocalDate;
	onDateChange: (value: LocalDate) => unknown;
	type: CalendarType;
	view: CalendarViewType;
}

const WeekFormat = DateTimeFormatter.ofPattern("'Week 'w' of 'YYYY").withLocale(
	Locale.US,
);
const MonthFormat = DateTimeFormatter.ofPattern("MMMM yyyy").withLocale(
	Locale.US,
);

const [CalendarProvider, useCalendar] = constate(
	({ date, onDateChange, type, view }: CalendarProviderProps) => {
		const navigate = useNavigate();
		const [platformFilter, setPlatformFilter] = useState<
			Set<PlatformValue> | undefined
		>(undefined);

		const handleNext = useEventCallback(() => {
			onDateChange(type === "month" ? date.plusMonths(1) : date.plusWeeks(1));
		});

		const handlePrev = useEventCallback(() => {
			onDateChange(type === "month" ? date.minusMonths(1) : date.minusWeeks(1));
		});

		const handleCalendarType = useEventCallback((newType: CalendarType) =>
			navigate(LinkGen.Calendar(date, newType, view)),
		);
		const handleCalendarView = useEventCallback(() =>
			navigate(LinkGen.Calendar(date, type, "calendar")),
		);
		const handleListView = useEventCallback(() =>
			navigate(LinkGen.Calendar(date, type, "list")),
		);

		const days = useMemo(
			() => (type === "month" ? getMonthGridDays(date) : getWeekGridDays(date)),
			[type, date],
		);

		const label = useMemo(() => {
			return date.format(type === "week" ? WeekFormat : MonthFormat);
		}, [date, type]);

		const togglePlatformFilter = useCallback((platform: PlatformValue) => {
			setPlatformFilter((prev) => {
				const next = new Set(prev ?? []);
				if (next.has(platform)) {
					next.delete(platform);
				} else {
					next.add(platform);
				}
				// Empty set = show all (clear filter)
				return next.size === 0 ? undefined : next;
			});
		}, []);

		const clearPlatformFilter = useCallback(() => {
			setPlatformFilter(undefined);
		}, []);

		const filterPosts = useCallback(
			(posts: PostDto[]) => {
				return posts.filter((x) => {
					const postDate = getPostDate(x).toLocalDate();
					if (!days.some((day) => day.isEqual(postDate))) {
						return false;
					}
					if (platformFilter && !platformFilter.has(x.platform)) {
						return false;
					}
					return true;
				});
			},
			[days, platformFilter],
		);

		return {
			date,
			days,
			view,
			type,
			handleNext,
			handlePrev,
			handleCalendarView,
			handleListView,
			filterPosts,
			handleCalendarType,
			label,
			platformFilter,
			togglePlatformFilter,
			clearPlatformFilter,
		};
	},
);
export { CalendarProvider, useCalendar };
